import React from 'react';

import ContentLayout from '@amzn/awsui-components-console/content-layout';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Header from '@amzn/awsui-components-console/header';
import Alert from '@amzn/awsui-components-console/alert';
import Button from '@amzn/awsui-components-console/button';

import TrackFilters from './components/TrackFilters';
import SavedTrackSearchesBar from './components/SavedTrackSearchesBar';
import SaveTrackSearchModal from './components/SaveTrackSearchModal';
import ActiveJobsTable from './components/ActiveJobsTable';
import HistoricalJobsTable from './components/HistoricalJobsTable';
import { useTrackFilters } from './hooks/useTrackFilters';
import { useTrackData } from './hooks/useTrackData';
import { TRACK_STRING_IDS } from './constants';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import {
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import { useTranslation } from 'src/i18n/shim/useTranslation';

const TrackTranslationsPage: React.FC = () => {
  const { t } = useTranslation();
  const filters = useTrackFilters();
  const trackData = useTrackData(filters.appliedFilters);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t(TRACK_STRING_IDS.PAGE_DESCRIPTION)}>
          {t(TRACK_STRING_IDS.PAGE_TITLE)}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {filters.namespacesError && (
          <Alert type="error" header={t('nav-load-failed-header')}>
            {t('track-namespaces-load-failed-body')}
          </Alert>
        )}

        {filters.stringGroupsError && (
          <Alert type="error" header={t('track-string-groups-load-failed')}>
            {t('track-string-groups-load-failed-body')}
          </Alert>
        )}

        <TrackFilters
          namespace={filters.namespace}
          onNamespaceChange={filters.setNamespace}
          stringGroup={filters.stringGroup}
          onStringGroupChange={filters.setStringGroup}
          dateRange={filters.dateRange}
          onDateRangeChange={filters.setDateRange}
          onApply={filters.applyFilters}
          onSaveSearch={filters.openSaveModal}
          namespaceOptions={filters.namespaceOptions}
          stringGroupOptions={filters.stringGroupOptions}
          loading={filters.namespacesLoading}
          stringGroupsLoading={filters.stringGroupsLoading}
        />

        {/* Saved searches sit directly below the filter card, per track.html.
            Persisted in the users DDB table via GetUserPreference/UpdateUserPreference. */}
        <SavedTrackSearchesBar onLoadSearch={filters.loadSavedSearch} />

        <SaveTrackSearchModal
          visible={filters.saveModalVisible}
          name={filters.saveSearchName}
          onNameChange={filters.setSaveSearchName}
          onConfirm={filters.confirmSaveSearch}
          onCancel={filters.cancelSaveSearch}
          isSaving={filters.isSaving}
          errorText={filters.duplicateError}
        />

        {filters.appliedFilters.namespace && (
          <SpaceBetween size="l">
            {/*
              A refusal gets the service's own wording and NO Retry: the identical
              request would be refused again, so the button would only invite the
              user to fail twice. The generic branch keeps its Retry, which is the
              right affordance for a transient fault.
            */}
            {trackData.activeError &&
              (isAccessDenied(trackData.activeError) ? (
                <AccessDeniedAlert
                  message={getAccessDeniedMessage(trackData.activeError)}
                  deniedNamespace={getAccessDeniedNamespace(
                    trackData.activeError,
                  )}
                  data-testid="active-jobs-access-denied"
                />
              ) : (
                <Alert
                  type="error"
                  header={t(TRACK_STRING_IDS.ERROR_ACTIVE_HEADER)}
                  action={
                    <Button onClick={() => trackData.retryActiveJobs()}>
                      {t('search-filter-retry')}
                    </Button>
                  }
                >
                  {t(TRACK_STRING_IDS.ERROR_BODY)}
                </Alert>
              ))}
            <ActiveJobsTable
              items={trackData.activeError ? [] : trackData.activeJobs}
              loading={trackData.activeJobsLoading}
              isLoadingMore={trackData.activeIsLoadingMore}
              hasMore={trackData.activeHasMore}
              onLoadMore={trackData.loadMoreActiveJobs}
            />

            {trackData.historicalError &&
              (isAccessDenied(trackData.historicalError) ? (
                <AccessDeniedAlert
                  message={getAccessDeniedMessage(trackData.historicalError)}
                  deniedNamespace={getAccessDeniedNamespace(
                    trackData.historicalError,
                  )}
                  data-testid="historical-jobs-access-denied"
                />
              ) : (
                <Alert
                  type="error"
                  header={t(TRACK_STRING_IDS.ERROR_HISTORICAL_HEADER)}
                  action={
                    <Button onClick={() => trackData.retryHistoricalJobs()}>
                      {t('search-filter-retry')}
                    </Button>
                  }
                >
                  {t(TRACK_STRING_IDS.ERROR_BODY)}
                </Alert>
              ))}
            <HistoricalJobsTable
              items={trackData.historicalError ? [] : trackData.historicalJobs}
              loading={trackData.historicalJobsLoading}
              isLoadingMore={trackData.historicalIsLoadingMore}
              hasMore={trackData.historicalHasMore}
              onLoadMore={trackData.loadMoreHistoricalJobs}
            />
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};

export default TrackTranslationsPage;
